import type { ElementType, ReactNode } from "react";
import { RadioGroup } from "@headlessui/react";
import { TippyV2 } from "@/react/components/shared/TippyV2";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Typography } from "@circle-react-uikit/Typography";

const sizeClasses = {
  sm: "px-2 py-1.5",
  md: "px-3 py-2",
} as const;

interface Option<T extends string = string> {
  ariaLabel?: string;
  label: ReactNode;
  tooltipText?: string;
  value: T;
}

export interface SelectRadioGroupProps<T extends string = string> {
  checkedClassName?: string;
  className?: string;
  onChange: (value: T) => void;
  optionClassName?: string;
  options: Option<T>[];
  size?: keyof typeof sizeClasses;
  value: T;
}

export const SelectRadioGroup = <T extends string = string>({
  value,
  onChange,
  options,
  size = "md",
  className = "",
  checkedClassName = "bg-tertiary",
  optionClassName = "",
}: SelectRadioGroupProps<T>) => {
  const sizeClass = sizeClasses[size] ?? sizeClasses.md;

  return (
    <RadioGroup<ElementType, T> value={value} onChange={onChange}>
      <div className={classNames("flex items-center gap-6", className)}>
        {options.map(({ label, value, tooltipText }) => (
          <RadioGroup.Option
            key={value}
            value={value}
            className={({ checked }) =>
              classNames(
                "hover:bg-tertiary",
                "focus:bg-tertiary focus:ring-primary focus:ring-2 focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2",
                "flex cursor-pointer select-none rounded-lg leading-none transition-colors",
                sizeClass,
                {
                  [checkedClassName]: checked,
                },
                optionClassName,
              )
            }
          >
            {({ checked }) => {
              const labelNode =
                typeof label === "string" ? (
                  <Typography.LabelSm weight={checked ? "medium" : "normal"}>
                    {label}
                  </Typography.LabelSm>
                ) : (
                  label
                );
              return (
                <RadioGroup.Label
                  as="span"
                  className={classNames("flex whitespace-nowrap", {
                    "text-light": !checked,
                    "text-dark": checked,
                  })}
                  {...(checked && { "aria-checked": checked })}
                >
                  {tooltipText ? (
                    <TippyV2
                      content={tooltipText}
                      arrow={false}
                      delay={100}
                      duration={100}
                    >
                      {labelNode}
                    </TippyV2>
                  ) : (
                    labelNode
                  )}
                </RadioGroup.Label>
              );
            }}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
};
